import { trpc } from "@/lib/trpc";
import { checkTradingHours, smartInterval } from "@/lib/timeUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  TrendingUp, TrendingDown, Activity, Target, BarChart3,
  ArrowUpRight, ArrowDownRight, Clock, Zap, Shield,
  ChevronDown, ChevronUp, Minus, AlertTriangle, Trophy,
  PieChart, Calendar, RefreshCw,
} from "lucide-react";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";

// ========== 时间范围选项 ==========
const TIME_RANGES = [
  { label: "7天", days: 7 },
  { label: "30天", days: 30 },
  { label: "90天", days: 90 },
  { label: "全部", days: 365 },
] as const;

// ========== SVG 收益曲线图 ==========
function EquityCurveChart({ data }: { data: { date: string; equity: number; dailyPnl: number; trades: number }[] }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 200 });

  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      if (width > 0) setDimensions({ width, height: 200 });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const { width, height } = dimensions;
  const padding = { top: 20, right: 20, bottom: 30, left: 55 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const { pathEquity, pathZero, yLabels, points } = useMemo(() => {
    if (data.length === 0) return { pathEquity: "", pathZero: "", yLabels: [], points: [] };

    const equities = data.map(d => d.equity);
    const minY = Math.min(0, ...equities);
    const maxY = Math.max(0, ...equities);
    const range = maxY - minY || 1;

    const scaleX = (i: number) => padding.left + (i / Math.max(data.length - 1, 1)) * chartW;
    const scaleY = (v: number) => padding.top + chartH - ((v - minY) / range) * chartH;

    const pts = data.map((d, i) => ({ x: scaleX(i), y: scaleY(d.equity), ...d }));
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const zeroY = scaleY(0);
    const zeroPath = `M${padding.left},${zeroY.toFixed(1)} L${padding.left + chartW},${zeroY.toFixed(1)}`;

    // Y轴标签
    const step = range / 4;
    const labels = Array.from({ length: 5 }, (_, i) => {
      const val = minY + step * i;
      return { y: scaleY(val), label: val >= 1000 || val <= -1000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(0) };
    });

    return { pathEquity: path, pathZero: zeroPath, yLabels: labels, points: pts };
  }, [data, width, height]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        暂无交易数据，启动Agent后将自动记录
      </div>
    );
  }

  const hovered = hoveredIdx !== null ? points[hoveredIdx] : null;

  return (
    <div className="relative w-full">
      <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`}
        className="select-none"
        onMouseMove={e => {
          const rect = svgRef.current?.getBoundingClientRect();
          if (!rect || points.length === 0) return;
          const mx = e.clientX - rect.left;
          const ratio = (mx - padding.left) / chartW;
          const idx = Math.round(ratio * (points.length - 1));
          setHoveredIdx(Math.max(0, Math.min(points.length - 1, idx)));
        }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {/* 网格线 */}
        {yLabels.map((l, i) => (
          <g key={i}>
            <line x1={padding.left} y1={l.y} x2={padding.left + chartW} y2={l.y}
              stroke="currentColor" strokeOpacity={0.06} strokeDasharray="3,3" />
            <text x={padding.left - 8} y={l.y + 3} textAnchor="end"
              className="fill-muted-foreground" fontSize={10}>{l.label}</text>
          </g>
        ))}

        {/* 零线 */}
        <path d={pathZero} stroke="currentColor" strokeOpacity={0.15} strokeWidth={1} fill="none" />

        {/* 收益曲线 - 渐变填充 */}
        <defs>
          <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={data[data.length - 1]?.equity >= 0 ? "#10b981" : "#ef4444"} stopOpacity={0.3} />
            <stop offset="100%" stopColor={data[data.length - 1]?.equity >= 0 ? "#10b981" : "#ef4444"} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {points.length > 0 && (
          <path
            d={`${pathEquity} L${points[points.length - 1].x.toFixed(1)},${padding.top + chartH} L${points[0].x.toFixed(1)},${padding.top + chartH} Z`}
            fill="url(#equityGrad)"
          />
        )}
        <path d={pathEquity}
          stroke={data[data.length - 1]?.equity >= 0 ? "#10b981" : "#ef4444"}
          strokeWidth={2} fill="none" strokeLinejoin="round" />

        {/* X轴日期标签 */}
        {data.length > 0 && (() => {
          const step = Math.max(1, Math.floor(data.length / 6));
          const indices = [];
          for (let i = 0; i < data.length; i += step) indices.push(i);
          if (indices[indices.length - 1] !== data.length - 1) indices.push(data.length - 1);
          return indices.map(i => (
            <text key={i} x={points[i].x} y={height - 5} textAnchor="middle"
              className="fill-muted-foreground" fontSize={9}>
              {data[i].date.slice(5)}
            </text>
          ));
        })()}

        {/* 悬停指示器 */}
        {hovered && (
          <>
            <line x1={hovered.x} y1={padding.top} x2={hovered.x} y2={padding.top + chartH}
              stroke="currentColor" strokeOpacity={0.2} strokeDasharray="2,2" />
            <circle cx={hovered.x} cy={hovered.y} r={4}
              fill={hovered.equity >= 0 ? "#10b981" : "#ef4444"} stroke="white" strokeWidth={1.5} />
          </>
        )}
      </svg>

      {/* 悬停信息框 */}
      {hovered && (
        <div className="absolute top-2 right-2 bg-popover/95 backdrop-blur-sm border border-border rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none">
          <div className="text-muted-foreground mb-1">{hovered.date}</div>
          <div className="flex items-center gap-3">
            <span>累计: <span className={`font-mono font-bold ${hovered.equity >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {hovered.equity >= 0 ? '+' : ''}{hovered.equity.toFixed(2)}
            </span></span>
            <span>当日: <span className={`font-mono ${hovered.dailyPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {hovered.dailyPnl >= 0 ? '+' : ''}{hovered.dailyPnl.toFixed(2)}
            </span></span>
            <span className="text-muted-foreground">{hovered.trades}笔</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== 小时分布柱状图 ==========
function HourlyChart({ data }: { data: { hour: number; trades: number; pnl: number }[] }) {
  if (data.length === 0) return <div className="text-center text-muted-foreground text-xs py-4">暂无数据</div>;

  const maxTrades = Math.max(...data.map(d => d.trades), 1);

  return (
    <div className="flex items-end gap-0.5 h-[80px] px-1">
      {Array.from({ length: 24 }, (_, h) => {
        const d = data.find(x => x.hour === h);
        if (h < 9 || h > 15) return null; // 只显示交易时段
        const trades = d?.trades || 0;
        const pnl = d?.pnl || 0;
        const barH = trades > 0 ? Math.max(4, (trades / maxTrades) * 70) : 0;
        return (
          <TooltipProvider key={h}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center flex-1 min-w-0">
                  <div
                    className={`w-full rounded-t-sm transition-all ${pnl >= 0 ? 'bg-emerald-500/60' : 'bg-red-500/60'}`}
                    style={{ height: `${barH}px` }}
                  />
                  <span className="text-[8px] text-muted-foreground mt-0.5">{h}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p>{h}:00 - {trades}笔交易</p>
                <p className={pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                  盈亏: {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// ========== 主组件 ==========
export default function SimPerformanceDashboard() {
  const [days, setDays] = useState(30);
  const [showAgentDetail, setShowAgentDetail] = useState(false);

  const _isTH = checkTradingHours().isTradingHours;
  const { data, isLoading, refetch } = trpc.simPerformance.dashboard.useQuery(
    { days },
    { refetchInterval: smartInterval(30000, _isTH, 120000) }
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-[240px]" />
      </div>
    );
  }

  if (!data) return null;

  const { overview, equityCurve, agentBreakdown, hourlyDistribution, scenarioBreakdown } = data;

  return (
    <div className="space-y-4">
      {/* 标题栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">模拟交易绩效</h3>
          <Badge variant="outline" className="text-[10px]">
            {overview.activeAgents}/{overview.totalAgents} Agent运行中
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {TIME_RANGES.map(r => (
            <Button key={r.days} variant={days === r.days ? "default" : "ghost"} size="sm"
              className="h-7 text-xs px-2" onClick={() => setDays(r.days)}>
              {r.label}
            </Button>
          ))}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* 总盈亏 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              {overview.totalPnl >= 0
                ? <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                : <TrendingDown className="h-3.5 w-3.5 text-red-400" />}
              <span className="text-[10px] text-muted-foreground">累计盈亏</span>
            </div>
            <div className={`text-xl font-bold font-mono ${overview.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {overview.totalPnl >= 0 ? '+' : ''}¥{overview.totalPnl.toFixed(2)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              手续费: ¥{overview.totalFees.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        {/* 胜率 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Target className="h-3.5 w-3.5 text-blue-400" />
              <span className="text-[10px] text-muted-foreground">胜率</span>
            </div>
            <div className="text-xl font-bold font-mono text-foreground">
              {overview.winRate.toFixed(1)}%
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {overview.totalTrades}笔 · {overview.totalRounds}轮
            </div>
          </CardContent>
        </Card>

        {/* 盈亏比 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-[10px] text-muted-foreground">盈亏比</span>
            </div>
            <div className={`text-xl font-bold font-mono ${overview.profitFactor >= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
              {overview.profitFactor >= 999 ? '∞' : overview.profitFactor.toFixed(2)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              均盈: ¥{overview.avgPnlPerTrade.toFixed(2)}/笔
            </div>
          </CardContent>
        </Card>

        {/* 夏普比率 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Activity className="h-3.5 w-3.5 text-purple-400" />
              <span className="text-[10px] text-muted-foreground">夏普比率</span>
            </div>
            <div className={`text-xl font-bold font-mono ${overview.sharpeRatio >= 0 ? 'text-foreground' : 'text-red-400'}`}>
              {overview.sharpeRatio.toFixed(2)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              年化 · 无风险3%
            </div>
          </CardContent>
        </Card>

        {/* 最大回撤 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <ArrowDownRight className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-[10px] text-muted-foreground">最大回撤</span>
            </div>
            <div className="text-xl font-bold font-mono text-orange-400">
              ¥{overview.maxDrawdown.toFixed(2)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {overview.maxDrawdownPct.toFixed(1)}%
            </div>
          </CardContent>
        </Card>

        {/* 最佳日 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Trophy className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-[10px] text-muted-foreground">最佳日</span>
            </div>
            <div className="text-xl font-bold font-mono text-emerald-400">
              {overview.bestDay ? `+¥${overview.bestDay.pnl.toFixed(2)}` : '--'}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {overview.bestDay?.date?.slice(5) || '--'}
            </div>
          </CardContent>
        </Card>

        {/* 最差日 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
              <span className="text-[10px] text-muted-foreground">最差日</span>
            </div>
            <div className="text-xl font-bold font-mono text-red-400">
              {overview.worstDay ? `¥${overview.worstDay.pnl.toFixed(2)}` : '--'}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {overview.worstDay?.date?.slice(5) || '--'}
            </div>
          </CardContent>
        </Card>

        {/* 场景分布 */}
        <Card className="bg-muted/20 border-border/40">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <PieChart className="h-3.5 w-3.5 text-cyan-400" />
              <span className="text-[10px] text-muted-foreground">场景分布</span>
            </div>
            {scenarioBreakdown.length > 0 ? (
              <div className="space-y-0.5">
                {scenarioBreakdown.map(s => (
                  <div key={s.scenario} className="flex items-center justify-between text-[10px]">
                    <span className="text-muted-foreground truncate">{s.scenario}</span>
                    <span className={`font-mono ${s.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {s.count}笔 {s.winRate}%
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">--</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 收益曲线 */}
      <Card className="bg-muted/20 border-border/40">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            收益曲线
            {equityCurve.length > 0 && (
              <Badge variant="outline" className="text-[9px] ml-auto">
                {equityCurve.length}个交易日
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 pb-3">
          <EquityCurveChart data={equityCurve} />
        </CardContent>
      </Card>

      {/* 下方两栏：小时分布 + Agent分项 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* 交易时段分布 */}
        <Card className="bg-muted/20 border-border/40">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-400" />
              交易时段分布
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <HourlyChart data={hourlyDistribution} />
          </CardContent>
        </Card>

        {/* Agent分项绩效 */}
        <Card className="bg-muted/20 border-border/40">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-amber-400" />
              Agent分项绩效
              <Button variant="ghost" size="sm" className="h-6 text-[10px] ml-auto px-2"
                onClick={() => setShowAgentDetail(!showAgentDetail)}>
                {showAgentDetail ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showAgentDetail ? '收起' : '展开'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {agentBreakdown.length === 0 ? (
              <div className="text-center text-muted-foreground text-xs py-4">暂无Agent交易数据</div>
            ) : (
              <div className="space-y-1.5">
                {(showAgentDetail ? agentBreakdown : agentBreakdown.slice(0, 5)).map(a => (
                  <div key={a.monitorId} className="flex items-center justify-between text-xs bg-muted/20 rounded-md px-2.5 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`h-1.5 w-1.5 rounded-full ${a.isRunning ? 'bg-emerald-400 animate-pulse' : 'bg-muted-foreground'}`} />
                      <span className="font-medium truncate">{a.stockName}</span>
                      <span className="text-muted-foreground text-[10px]">{a.stockCode}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-muted-foreground">{a.trades}笔</span>
                      <span className="text-muted-foreground">胜率{a.winRate}%</span>
                      <span className={`font-mono font-medium ${a.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {a.totalPnl >= 0 ? '+' : ''}{a.totalPnl.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
                {!showAgentDetail && agentBreakdown.length > 5 && (
                  <div className="text-center text-muted-foreground text-[10px] py-1">
                    还有 {agentBreakdown.length - 5} 个Agent...
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
